// windowStore — position/size/mode/stacking for the app's movable windows.
//
// Rebecca's feedback (2026-08-06, #dailies): the Settings window couldn't be
// moved, so tuning a scene colour meant type → close → look → reopen. The fix
// is a general capability, not a Settings patch — any panel that registers here
// gets the Unreal window model: DOCKED in its host region, FLOATING and
// draggable over the shell, or POPPED into a real OS window (second monitor).
//
// Same module-singleton + subscribe shape as shellLayout/sceneSelection (the
// repo idiom — no React state on shared UI state). One store for every window
// so z-order is global: clicking any window brings it above every other one,
// which per-window state could not express.
//
// Persistence is per window id under one key. Modes deliberately do NOT
// persist as 'popped' — restoring a session cannot reopen an OS window without
// a user gesture (popup blockers), so a popped window comes back 'floating':
// same content, still movable, no silent loss.

import { Z } from './zLayers.js'

const KEY = 'aos-windows-v1'

// A window you just grabbed must not slide UNDER the chrome or a fullscreen
// modal; dialogs stay above it in turn. The whole scale lives in zLayers now —
// this comment used to BE the scale, which no overlay could import a number
// from, so every one of them invented its own and some landed under the menubar.
export const WINDOW_Z_BASE = Z.WINDOW_BASE

export const MIN_W = 320
export const MIN_H = 220
// While dragging, at least this much of the title bar must stay reachable —
// a window whose only handle is off-screen is a window you can never retrieve.
export const EDGE_KEEP = 64
// …and vertically, this much. Both are POLICY MINIMUMS — how much of a window
// the app refuses to let you lose — not copies of any bar's real height. That
// distinction is what makes them legitimate constants: nothing else in the app
// defines them, so nothing can drift out from under them. A number that
// DESCRIBES something (a bar's height, the chrome's height) is measured
// instead — see chromeTop below, and DraggablePanel's bar.
export const BAR_KEEP = 40
// A window OPENS below the top chrome, never flush to the top and never
// covering the menu it was opened from — Rebecca's whole ask was to see the
// shell while a window is up. This is the open default only; a user may still
// DRAG a window up to the top edge (clampRect allows y=0).
//
// FALLBACK ONLY: the menubar is content-sized (padding + font) and grows with
// the UI scale, so 56 was a guess at something the DOM already knows — the same
// mistake as a hardcoded title-bar height, and one that puts a window UNDER the
// chrome at the larger font settings. chromeTop() measures it; this is what
// answers before there is a menubar to measure (boot, tests, the eye-test app).
export const TOP_MARGIN = 56

// Gap between an edge-anchored window and the viewport edge.
export const EDGE_MARGIN = 12

/**
 * The y a window opens at: below the app's top chrome, measured.
 *
 * Returns window units (the menubar lays out inside the same UI-scale zoom the
 * windows do, and getBoundingClientRect reports zoomed px).
 *
 * Found by `data-app-chrome`, an attribute the menubar declares for this — not
 * by its test id, which belongs to the specs and can be renamed with one.
 */
export function chromeTop(win = typeof window !== 'undefined' ? window : null) {
  try {
    const bar = win?.document?.querySelector('[data-app-chrome="menubar"]')
    if (!bar) return TOP_MARGIN
    const { zoom } = viewportSize(win)
    const h = bar.getBoundingClientRect().height / zoom
    return h > 0 ? Math.round(h + EDGE_MARGIN) : TOP_MARGIN
  } catch { return TOP_MARGIN }
}

// placedVw/placedVh stamp the viewport a window's geometry was PLACED in — see
// viewportChangedSince. Null on a record written before the stamp existed.
// `restore` is the rect a MAXIMIZED window goes back to — its presence IS the
// maximized flag, so the two can never disagree.
const DEFAULTS = Object.freeze({ x: null, y: null, w: null, h: null, mode: 'docked', placedVw: null, placedVh: null, restore: null })

/**
 * The mode a window actually PRESENTS, which is not always the stored one.
 *
 * A host that passes `dockable={false}` (Settings, Rig Preview) has no docked
 * presentation to fall back on, so a stored 'docked' has to read as floating —
 * and 'docked' is DEFAULTS.mode, i.e. exactly what every fresh profile holds
 * the first time such a window is opened.
 *
 * This is ONE function because it used to be two expressions. FloatingWindow's
 * render coerced the stored mode, while the drag guard read `windowStore.get(id).mode`
 * raw (0c5623a, which merged two drag handlers into one and kept the wrong
 * half). The result was a window that rendered floating, showed a 'move'
 * cursor, and silently refused to move — on a fresh profile, which is to say
 * for every new user. Anything asking "is this window floating?" must ask here.
 */
export function effectiveMode(storedMode, dockable = true) {
  if (dockable) return storedMode
  return storedMode === 'docked' ? 'floating' : storedMode
}

function load() {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch { return {} }
}

let windows = typeof window !== 'undefined' ? load() : {}
let topZ = WINDOW_Z_BASE
const listeners = new Set()

function persist() {
  try {
    // z is session stacking, not layout — strip it so a reload starts flat.
    const out = {}
    for (const [id, w] of Object.entries(windows)) {
      const { z, ...rest } = w
      // 'popped' does not survive a reload (see header) — store as floating.
      out[id] = rest.mode === 'popped' ? { ...rest, mode: 'floating' } : rest
    }
    localStorage.setItem(KEY, JSON.stringify(out))
  } catch { /* quota/private mode — window state is a nicety, not data */ }
}

function notify() { for (const fn of listeners) fn() }

/**
 * Resize a window rect by dragging one edge or corner, OS-window style.
 *
 * `dir` is a compass string built from n/s/e/w ('n', 'se', 'nw', …); dx/dy are
 * the pointer's total movement since the drag started.
 *
 * The rule that makes this feel right is ANCHORING: the edge you are NOT
 * dragging must not move. Dragging the west edge changes x AND w so the east
 * edge stays put; dragging north changes y AND h so the south edge stays put.
 * Without that the window slides across the screen while you resize it.
 *
 * The minimum size pins the DRAGGED edge, never the anchor — otherwise a
 * window shrunk past the minimum from the left creeps rightwards.
 */
export function resizeRect({ x, y, w, h }, dir, dx, dy) {
  let nx = x, ny = y, nw = w, nh = h

  if (dir.includes('e')) nw = w + dx
  if (dir.includes('s')) nh = h + dy
  if (dir.includes('w')) { nw = w - dx; nx = x + dx }
  if (dir.includes('n')) { nh = h - dy; ny = y + dy }

  if (nw < MIN_W) {
    if (dir.includes('w')) nx = x + w - MIN_W   // hold the east edge
    nw = MIN_W
  }
  if (nh < MIN_H) {
    if (dir.includes('n')) ny = y + h - MIN_H   // hold the south edge
    nh = MIN_H
  }

  // Same rule as clampRect: the title bar is the only drag handle and it is at
  // the top, so a negative y is unrecoverable. Give the overshoot back to the
  // height so the south edge still does not move.
  if (ny < 0) {
    nh = Math.max(MIN_H, nh + ny)
    ny = 0
  }

  return { x: nx, y: ny, w: nw, h: nh }
}

/**
 * Move a window by a SCREEN delta (clientX/clientY differences).
 *
 * The conversion is the whole point. A window's stored x/y/w/h are in its OWN
 * units — `left: win.x` on a fixed element under the app root's UI-scale zoom
 * paints at win.x * zoom, which is why viewportSize() below divides innerWidth
 * by that zoom and why centreRect centres in the divided space. But a pointer
 * delta arrives in SCREEN pixels, and adding it raw to a local coordinate made
 * a dragged window travel `zoom` times as far as the cursor: at the default
 * step of 1.1 it visibly outran the pointer, by 10% of the distance dragged.
 *
 * The clamp has to be in the same units for the same reason — handed a raw
 * innerWidth it believed the viewport was `zoom` times wider than it is, so a
 * window could be dragged clean past the right edge.
 *
 * @param {object} start   the rect the drag began from, in window units
 * @param {number} dxScreen horizontal pointer movement, in screen px
 * @param {number} dyScreen vertical pointer movement, in screen px
 * @param {number} zoom     cumulative CSS zoom at the window (see uiZoomCoords)
 * @param {{innerWidth:number, innerHeight:number}} viewport screen-px viewport
 */
export function dragRect(start, dxScreen, dyScreen, zoom, viewport) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  return clampRect(
    { ...start, x: (start.x ?? 0) + dxScreen / z, y: (start.y ?? 0) + dyScreen / z },
    viewport.innerWidth / z,
    viewport.innerHeight / z,
  )
}

/**
 * Convert a DOM rect into window units.
 *
 * getBoundingClientRect reports ZOOMED pixels. The resize path read its
 * starting rect from the DOM and stored it back unconverted, so the first store
 * write of a resize replaced a local coordinate with a zoomed one — the window
 * leapt by (zoom - 1) x its position and inflated by the zoom factor before the
 * user had actually resized anything.
 */
export function rectToWindowUnits({ left, top, width, height }, zoom) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  return { x: left / z, y: top / z, w: width / z, h: height / z }
}

/** Resize by a SCREEN delta, from a start rect already in window units. */
export function resizeByScreenDelta(start, dir, dxScreen, dyScreen, zoom) {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1
  return resizeRect(start, dir, dxScreen / z, dyScreen / z)
}

/** Clamp a window rect so its title bar stays reachable in a vw×vh viewport. */
export function clampRect({ x, y, w, h }, vw, vh) {
  const width = Math.max(MIN_W, Math.min(w ?? MIN_W, vw))
  const height = Math.max(MIN_H, Math.min(h ?? MIN_H, vh))
  return {
    w: width,
    h: height,
    // Horizontally a window may hang off either edge as long as EDGE_KEEP of
    // it remains on-screen; vertically the BAR must stay below 0 (you drag by
    // the top, so off the top = unreachable) and above the bottom edge.
    x: Math.max(-(width - EDGE_KEEP), Math.min(x ?? 0, vw - EDGE_KEEP)),
    y: Math.max(0, Math.min(y ?? 0, vh - BAR_KEEP)),
  }
}

/**
 * Centre a w×h window in a vw×vh viewport (the first-open default).
 *
 * `top` is where the chrome ends — measured by the caller (chromeTop), with
 * TOP_MARGIN as the fallback so this stays a pure function.
 */
export function centreRect(w, h, vw, vh, top = TOP_MARGIN) {
  const width = Math.min(w, vw - 2 * EDGE_MARGIN)
  // The height has to fit BELOW the top margin, not merely inside the viewport.
  // Clamping against `vh` alone and then flooring y at the margin let a tall
  // window hang off the bottom by exactly top - (vh - h) / 2, with no way to
  // reach what was under there: you drag a window by its title bar, and that
  // bar is at the TOP, so dragging up is the one direction clampRect refuses.
  // Measured on Version Control at the default UI scale: 1232x694 placed at
  // y=62 in a 720px viewport, 36px past the bottom.
  const height = Math.min(h, vh - top - EDGE_MARGIN)
  return { w: width, h: height, x: Math.round((vw - width) / 2), y: Math.round(Math.max(top, (vh - height) / 2)) }
}

/**
 * The viewport as the WINDOWS see it, in their own coordinate space.
 *
 * The app root carries a CSS zoom for UI scale (uiScale.js, published as
 * --aos-ui-zoom), and floating windows lay out inside it — so a window placed
 * at x renders at x * zoom, while `window.innerWidth` still reports unzoomed
 * pixels. Anchoring against the raw innerWidth therefore pushes a
 * right-aligned window off-screen at any scale above 1.0. Dividing by the zoom
 * puts both sides of the arithmetic in the same space.
 */
export function viewportSize(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return { vw: 0, vh: 0, zoom: 1 }
  let zoom = 1
  try {
    const raw = win.getComputedStyle?.(win.document.documentElement)
      ?.getPropertyValue('--aos-ui-zoom')
    const parsed = parseFloat(raw)
    if (Number.isFinite(parsed) && parsed > 0) zoom = parsed
  } catch { /* no document (tests) — unzoomed */ }
  return { vw: win.innerWidth / zoom, vh: win.innerHeight / zoom, zoom }
}

/**
 * First-open placement for a window that must NOT cover the middle of the
 * viewport — a panel you operate while watching the avatar (the Rig Preview
 * face controls) is useless centred on top of the face it is posing.
 *
 * 'centre' is the default everywhere and is exactly centreRect; 'right' parks
 * the window against the right edge, below the menu bar. Only the FIRST open
 * uses this — once the user drags, their position is what persists.
 */
export function anchoredRect(w, h, vw, vh, anchor = 'centre', top = TOP_MARGIN) {
  const rect = centreRect(w, h, vw, vh, top)
  if (anchor !== 'right') return rect
  return { ...rect, x: Math.round(Math.max(0, vw - rect.w - EDGE_MARGIN)), y: top }
}

/**
 * Pull a stored rect back INTO a vw x vh viewport.
 *
 * clampRect is the DRAG rule: it only guarantees EDGE_KEEP px of the bar stay
 * reachable, because a window the user deliberately parked half off the right
 * edge must stay where they put it. This is the OPEN / VIEWPORT-CHANGED rule,
 * and it is stricter: the whole window comes back on-screen where it fits.
 *
 * Why it has to exist: geometry persists per window id, and NOTHING re-measured
 * it when the viewport got smaller. Place a window against the right edge with
 * the browser maximised (Rig Preview anchors there by default), then un-maximise
 * or move to a smaller monitor, and its stored x is past the new innerWidth — so
 * the panel mounts, renders, and is invisible at position:fixed off the right
 * edge. It reads as "the window doesn't open", and it is unrecoverable by hand
 * because the title bar (the only drag handle) is off-screen too.
 *
 * A window LARGER than the viewport is shrunk to fit and pinned to 0 rather
 * than being given a negative origin — the bar stays at the top-left.
 *
 * The minimums are overridable for a caller whose surface has none: a resizable
 * WINDOW cannot go below MIN_W/MIN_H, but a content-sized panel has whatever
 * height its content gives it, and holding it to a 220px floor would reserve
 * bottom margin the panel never occupies (DraggablePanel passes its bar).
 */
export function clampIntoView({ x, y, w, h }, vw, vh, { minW = MIN_W, minH = MIN_H } = {}) {
  const width = Math.max(minW, Math.min(w ?? minW, Math.max(minW, vw)))
  const height = Math.max(minH, Math.min(h ?? minH, Math.max(minH, vh)))
  return {
    w: width,
    h: height,
    x: Math.round(Math.max(0, Math.min(x ?? 0, vw - width))),
    y: Math.round(Math.max(0, Math.min(y ?? 0, vh - height))),
  }
}

/**
 * Has the viewport changed enough since this window was placed that its
 * remembered position no longer means anything?
 *
 * A position is only meaningful relative to the viewport it was chosen in.
 * Park Rig Preview against the right edge on a 1920-wide monitor and reopen it
 * on a 1280-wide one and there is no position to restore — clamping lands it
 * against an edge it was never put at. Re-anchoring (back to the panel's own
 * default, keeping the size the user chose) is the honest answer there, and
 * clamping is the honest answer for the ordinary case where the viewport is
 * the same and the user genuinely did place it.
 *
 * The tolerance keeps small changes from throwing a window away: a scrollbar
 * or a chrome bar appearing is not a monitor change (DRIFT_FRACTION /
 * DRIFT_FLOOR — a proportion so it scales with the display, and a floor so a
 * small viewport still notices).
 *
 * A record with no stamp (written before this existed) reports false: an
 * upgrade must not fling every window a user had placed back to its anchor.
 */
// How much a viewport may change before a remembered position stops meaning
// anything. Judgement calls, deliberately: big enough that chrome appearing is
// ignored, small enough that an un-maximise is not.
const DRIFT_FRACTION = 0.05
const DRIFT_FLOOR = 24

export function viewportChangedSince(placed, vw, vh) {
  const pw = placed?.placedVw, ph = placed?.placedVh
  if (!(pw > 0) || !(ph > 0)) return false
  const tol = (a) => Math.max(DRIFT_FLOOR, a * DRIFT_FRACTION)
  return Math.abs(vw - pw) > tol(pw) || Math.abs(vh - ph) > tol(ph)
}

/**
 * Re-seat every window with stored geometry into the CURRENT viewport.
 *
 * Runs on resize (installed below) so un-maximising, a monitor change or a
 * devtools dock can never strand a window off-screen. Windows with no geometry
 * yet are left alone — their first open places them (anchoredRect).
 *
 * Writes (and notifies) only when something actually moved: a resize fires in
 * a stream and a store notify repaints every window that subscribes.
 *
 * Deliberately does NOT restamp placedVw/placedVh: this is a RESCUE, not a placement.
 * The stamp records the viewport the user last placed the window in, so the
 * next open still knows the position is stale and re-anchors (placeOnOpen).
 */
export function reclampAll(win = typeof window !== 'undefined' ? window : null) {
  if (!win) return false
  const { vw, vh } = viewportSize(win)
  if (!(vw > 0) || !(vh > 0)) return false
  let changed = false
  for (const [id, cur] of Object.entries(windows)) {
    if (cur.x == null || cur.y == null) continue
    const next = clampIntoView(cur, vw, vh)
    if (next.x !== cur.x || next.y !== cur.y || next.w !== cur.w || next.h !== cur.h) {
      windows[id] = { ...cur, ...next }
      changed = true
    }
  }
  if (changed) { persist(); notify() }
  return changed
}

/**
 * Place a window for THIS open. The one entry point every window goes through,
 * because persisted geometry is untrusted input and validating it in the chrome
 * component only covers the windows that use the chrome — the blueprint editor
 * surface renders its own frame from `win.x` and had no such guard.
 * (shellLayout says it best: the clamp IS the schema.)
 *
 * Three cases, in order:
 *
 *   no geometry        place at this window's own anchor
 *   viewport changed   re-anchor, keeping the size the user chose — a position
 *                      is only meaningful in the viewport it was picked in
 *   same viewport      the user's position, pulled fully on-screen
 *
 * Idempotent: writes (and notifies) only when the result differs from what is
 * stored, so calling it from a layout effect on every mode flip is free.
 *
 * @param {string} id
 * @param {{w:number, h:number, anchor?:'centre'|'right'}} defaults
 * @param {Window|null} win
 * @returns {'placed'|'reanchored'|'clamped'|'maximized'|'kept'|'no-viewport'}
 */
export function placeOnOpen(id, { w, h, anchor = 'centre' }, win = typeof window !== 'undefined' ? window : null) {
  const cur = windowStore.get(id)
  const { vw, vh } = viewportSize(win)
  const top = chromeTop(win)
  if (cur.restore && vw > 0 && vh > 0) {
    // Maximized means "fill the viewport", which is a rule, not a rect — so it
    // re-evaluates against the viewport we are actually opening into.
    if (cur.x === 0 && cur.y === 0 && cur.w === vw && cur.h === vh) return 'kept'
    windowStore.set(id, { x: 0, y: 0, w: vw, h: vh })
    return 'maximized'
  }
  if (cur.x == null || cur.y == null) {
    windowStore.set(id, anchoredRect(w, h, vw, vh, anchor, top))
    return 'placed'
  }
  if (!(vw > 0) || !(vh > 0)) return 'no-viewport'
  if (viewportChangedSince(cur, vw, vh)) {
    windowStore.set(id, anchoredRect(cur.w ?? w, cur.h ?? h, vw, vh, anchor, top))
    return 'reanchored'
  }
  const next = clampIntoView({ x: cur.x, y: cur.y, w: cur.w ?? w, h: cur.h ?? h }, vw, vh)
  if (next.x === cur.x && next.y === cur.y && next.w === cur.w && next.h === cur.h) return 'kept'
  windowStore.set(id, next)
  return 'clamped'
}

/**
 * Maximize a window to fill the viewport, or restore it to where it was.
 *
 * A window control, in the window chrome, because that is where it belongs: it
 * arrived here from Version Control, which had implemented its own inside a
 * "modal" — one of the tells that the thing was a window all along.
 *
 * The saved rect IS the flag (`restore`), so "maximized" cannot drift out of
 * sync with the geometry. Maximizing covers the app chrome, which is what
 * maximize means and is available to a window because windows sit above it.
 */
export function toggleMaximize(id, win = typeof window !== 'undefined' ? window : null) {
  const cur = windowStore.get(id)
  const { vw, vh } = viewportSize(win)
  if (cur.restore) {
    // The saved rect was measured in whatever viewport it was maximized FROM,
    // which may be a monitor ago — restoring it raw is the same stranding this
    // module exists to prevent. A window maximized before it had geometry has
    // nothing to restore to at all, so it is placed instead.
    const { x, y, w, h } = cur.restore
    const back = (x == null || y == null || !(vw > 0) || !(vh > 0))
      // Nothing to go back to: clear the geometry as well as the flag, so the
      // next open PLACES the window rather than leaving it viewport-sized and
      // no longer maximized — which reads as a restore that did nothing.
      ? { x: null, y: null, restore: null }
      : { ...clampIntoView({ x, y, w, h }, vw, vh), restore: null }
    windowStore.set(id, back)
    return 'restored'
  }
  if (!(vw > 0) || !(vh > 0)) return 'no-viewport'
  windowStore.set(id, {
    x: 0, y: 0, w: vw, h: vh,
    restore: { x: cur.x, y: cur.y, w: cur.w, h: cur.h },
  })
  return 'maximized'
}

/**
 * Where a popped-out OS window should open, in SCREEN coordinates.
 *
 * Two things have to be true at once:
 *
 *  - IT OPENS WHERE THE WINDOW ALREADY IS. Popping out is a change of frame,
 *    not a move: the panel should appear under the pointer that clicked ⧉,
 *    which is where it was sitting. Handing `rect` (the floating window's own
 *    viewport rect, in CSS px) does that — same place, same size, now an OS
 *    window. A window with no geometry of its own (a DOCKED editor surface)
 *    has no such place, and centres over the app instead.
 *
 *  - IT OPENS ON THE APP'S MONITOR. `window.open` with a size but no position
 *    is placed by the browser, and the placement is not opener-relative:
 *    Chrome cascades from the PRIMARY display's origin, so an app running on a
 *    second monitor pops its window out onto the other screen — which, to the
 *    user, is the popup not opening at all. Every result here is anchored to
 *    the opener's own screen origin, so it lands on the opener's display.
 *
 * The viewport's origin in screen space is derived from the opener's chrome:
 * the left/right borders split the outerWidth - innerWidth difference, and
 * whatever vertical space is left over is the tab strip + toolbar above the
 * page. Approximate by a few px on some platforms, which is the right kind of
 * error — the popup lands over the panel, not on another screen.
 */
export function popupPlacement(w, h, opener, rect = null) {
  const num = (v, fallback) => (Number.isFinite(v) ? v : fallback)
  const sx = num(opener?.screenX, 0)
  const sy = num(opener?.screenY, 0)
  const ow = num(opener?.outerWidth, w) || w
  const oh = num(opener?.outerHeight, h) || h
  if (rect && Number.isFinite(rect.x) && Number.isFinite(rect.y)) {
    const iw = num(opener?.innerWidth, ow)
    const ih = num(opener?.innerHeight, oh)
    const borderX = Math.max(0, (ow - iw) / 2)
    const chromeY = Math.max(0, oh - ih - borderX)
    return {
      left: Math.round(sx + borderX + rect.x),
      top: Math.round(sy + chromeY + rect.y),
    }
  }
  return {
    left: Math.round(sx + Math.max(0, (ow - w) / 2)),
    top: Math.round(sy + Math.max(0, (oh - h) / 2)),
  }
}

export const windowStore = {
  /** Full state for one window id (defaults applied, never undefined). */
  get(id) {
    return { ...DEFAULTS, z: WINDOW_Z_BASE, ...(windows[id] ?? {}) }
  },

  /** Patch one window's state. Geometry is clamped to the current viewport. */
  set(id, patch) {
    const cur = windowStore.get(id)
    let next = { ...cur, ...patch }
    if (typeof window !== 'undefined' && (patch.x != null || patch.y != null || patch.w != null || patch.h != null)) {
      // In WINDOW units, like every other coordinate here (viewportSize): handed
      // the raw innerWidth this clamp believed the viewport was `zoom` times
      // wider than it is, so a resize could size a window past the edge. Drags
      // were unaffected only because dragRect pre-clamps in the right space.
      const { vw, vh } = viewportSize()
      if (vw > 0 && vh > 0) {
        next = { ...next, ...clampRect(next, vw, vh) }
        // Stamp the viewport this placement was made in (viewportChangedSince).
        next = { ...next, placedVw: vw, placedVh: vh }
      }
    }
    windows[id] = next
    persist()
    notify()
  },

  /** Raise a window above every other one. */
  bringToFront(id) {
    topZ += 1
    windows[id] = { ...windowStore.get(id), z: topZ }
    notify()
  },

  subscribe(fn) {
    listeners.add(fn)
    return () => listeners.delete(fn)
  },

  /** Test seam. */
  _reset() {
    windows = {}
    topZ = WINDOW_Z_BASE
    try { localStorage.removeItem(KEY) } catch { /* ignore */ }
    notify()
  },
}

if (typeof window !== 'undefined') {
  window.__WINDOW_STORE__ = windowStore
  // One listener for every window: the store is the singleton that owns
  // geometry, so a panel that is currently unmounted is re-seated too.
  window.addEventListener('resize', () => reclampAll(window))
}

export default windowStore
